
/**
 * @summary - Component that Provides Stock Adjustment Edit Tab
 * @author Yuvasri S
 * @since 2024-07-26
 */
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from 'react-redux';
import { DefaultCard } from '../../assets/styled-components/card';
import { Box, Button, Grid, Stack } from '@mui/material';
import CustomTextField from '../commoncomponents/CustomTextField';
import CustomFooter from '../commoncomponents/CustomFooter';
import { MasterGraph } from "../../utility/apiURLS";
import {
    getAuthToken,
} from "../../utility/tokenUtility";
import axios from "axios";
import { resetEditStockAdjustment, updateStockUpdate, updateStockAdjustment } from "../../redux/reducers/StockRegisterReducer/stockSlice";
import CustomDatePicker from "../commoncomponents/CustomDatePicker";
import utils from "../../utility/utilities";
import CustomAgGrid from "../commoncomponents/CustomAgGrid";
import SectionTitle from "../commoncomponents/SectionTitle";
import CustomDialog from '../commoncomponents/CustomDialog';
import { useNavigate } from "react-router";
import dayjs from 'dayjs';

const StockAdjustmentEditTab = ({ stockDetails, isViewMode, setUnsavedChanges }) => {
    const [rows, setRows] = useState([]);
    const formatDateForDisplayGrid = (date) => {
        if (!date) {
            return '';  // Return empty string if no date is provided
        }

        let d;

        // Check if the date is an ISO string
        if (typeof date === 'string' && date.includes('T')) {
            d = new Date(date);
        } else {
            // Assume the date is in DD/MM/YYYY format
            const [day, month, year] = date.split('/').map(Number);
            d = new Date(year, month - 1, day);
        }

        // Validate the Date object
        if (isNaN(d.getTime())) {
            return '';  // Return empty string if the date is invalid
        }

        const day = String(d.getDate()).padStart(2, '0');
        const month = String(d.getMonth() + 1).padStart(2, '0');
        const year = d.getFullYear();
        return `${day}/${month}/${year}`;
    };

    const DateCellRenderer = (params) => {
        return formatDateForDisplayGrid(params.value);
    };

    const columns = [
        { field: "adjustmentno", headerName: "Adjustment No", width: 150 },
        {
            field: "adjustmentdate",
            headerName: "Adjustment Date",
            width: 180,
            cellRenderer: DateCellRenderer,
        },
        { field: "currentstocklevel", headerName: "Quality in System", width: 150 },
        { field: "physicalquantity", headerName: "Physical Quantity", width: 180 },
        { field: "variance", headerName: "Variance", width: 150 },
        { field: "adjustmentquantity", headerName: "Adjustment Quantity", width: 150 },
    ];

    const [gridApi, setGridApi] = useState(null);
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const currentDate = dayjs().format("DD/MM/YYYY");
    const initialStockAdjustmentState = {
        adjustmentNo: '',
        adjustmentDate: currentDate,
        quantityInSystem: '',
        physicalQuantity: '',
        variance: '',
        adjustmentQuantity: ''
    };
    const stockAdjustment = useSelector((state) => state.stock.stockAdjustment || initialStockAdjustmentState);
    const srId = useSelector((state) => state.stock.srPrimaryId);
    const [successDialogOpen, setSuccessDialogOpen] = useState(false);
    const [succesDialogMessage, setSuccessDialogMessage] = useState('');
    const token = getAuthToken("id_token");
    const config = {
        headers: {
            Authorization: `Bearer ${token}`,
        },
    };

    const {
        itemcodeId,
        itemcode,
        itemCodeDesc,
    } = stockDetails;

    const [errors, setErrors] = useState({
        physicalQuantity: false
    });

    const [errorMessages, setErrorMessages] = useState({
        physicalQuantity: ""
    });


    const [adId, setAdId] = useState('');
    const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
    const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
    const [cSL, setCsl] = useState('');

    const fetchStockAdjustment = async (srId) => {
        try {
            const response = await axios.get(
                `${MasterGraph}/api/GraphStockRegister/GetStockRegisterBystockadjustmentdetails/${srId}`,
                config
            );
            const stockAdjustment = response.data[0];

            if (stockAdjustment) {
                handleFieldChange('quantityInSystem', stockAdjustment.masimsstockregister.currentstocklevel || '');
                setAdId(stockAdjustment.id);
                setCsl(stockAdjustment.masimsstockregister.currentstocklevel)
                setHasUnsavedChanges(false);
            } else {
                dispatch(updateStockUpdate(initialStockAdjustmentState));
                setAdId('');
                setRows([]);
                setCsl('')
                setHasUnsavedChanges(false);
            }
        } catch (error) {
            console.error("Error fetching item details:", error);
            dispatch(updateStockAdjustment(initialStockAdjustmentState));
            setRows([]);
        }
    };
    const fetchGridRows = async (adjustmentId) => {
        try {
            const response = await axios.get(`${MasterGraph}/api/GraphStockRegister/GetStockadjustmentauditdetails/${adjustmentId}`, config);

            setRows(response.data); // Assuming response data is in the correct format
        } catch (error) {
            console.error("Error fetching grid rows:", error);
            setRows([]); // Clear the rows if there's an error
        }
    };


    useEffect(() => {
        if (srId) {
            fetchStockAdjustment(srId);
        } else {
            dispatch(updateStockAdjustment(initialStockAdjustmentState));
        }
    }, [srId, dispatch]);

    useEffect(() => {
        if (stockAdjustment.physicalQuantity) {
            const quantityInSystem = parseFloat(stockAdjustment.quantityInSystem);
            const physicalQuantity = parseFloat(stockAdjustment.physicalQuantity);
            const adjustmentQuantity = (-quantityInSystem) + physicalQuantity;
            const variance = Math.abs(adjustmentQuantity);

            handleFieldChange('adjustmentQuantity', adjustmentQuantity);
            handleFieldChange('variance', variance);
        } else {
            handleFieldChange('adjustmentQuantity', '');
            handleFieldChange('variance', '');
        }
    }, [stockAdjustment.quantityInSystem, stockAdjustment.physicalQuantity]);


    // Reset state on mount
    useEffect(() => {
        dispatch(resetEditStockAdjustment());
    }, [dispatch]);

    useEffect(() => {
        if (adId) {
            fetchGridRows(adId);
        }
    }, [adId]);


    // Format date to ensure it displays in DD/MM/YYYY format
    const formatDateForDisplay = (date) => {
        if (!date) {
            return currentDate;  // Default to current date if no date is provided
        }

        let d;

        // Check if the date is an ISO string
        if (typeof date === 'string' && date.includes('T')) {
            d = new Date(date);
        } else {
            // Assume the date is in DD/MM/YYYY format
            const [day, month, year] = date.split('/').map(Number);
            d = new Date(year, month - 1, day);
        }

        // Validate the Date object
        if (isNaN(d.getTime())) {
            return currentDate;  // Return current date if invalid
        }

        const day = String(d.getDate()).padStart(2, '0');
        const month = String(d.getMonth() + 1).padStart(2, '0');
        const year = d.getFullYear();
        return `${day}/${month}/${year}`;
    };

    const handleSave = async () => {
        try {
            // Convert to correct format for sending to the API
            const currentDate = dayjs().format("YYYY-MM-DD");  // Current date in ISO format

            // Parse the selected date correctly, assuming user input is in DD/MM/YYYY format
            const adjustmentDate = stockAdjustment.adjustmentDate
                ? dayjs(stockAdjustment.adjustmentDate, "DD/MM/YYYY").format("YYYY-MM-DD")
                : currentDate;

            const payload = {
                id: adId,
                adjustmentdate: adjustmentDate,  // Use the correctly formatted ISO date
                physicalquantity: stockAdjustment.physicalQuantity,
                variance: stockAdjustment.variance,
                adjustmentquantity: stockAdjustment.adjustmentQuantity
            };

            // PUT request to update the stock adjustment
            const putUrl = `${MasterGraph}/api/GraphStockRegister/UpdateStockAdjustment/${adId}`;
            await axios.put(putUrl, payload, config);
            setSuccessDialogMessage('Data updated successfully.');

            const getAdjustmentsUrl = `${MasterGraph}/api/GraphStockRegister/GetStockadjustmentauditdetails/${adId}`;

            // Fetch all records
            const { data: allAdjustments } = await axios.get(getAdjustmentsUrl, config);

            // Filter records by masimsstockadjustmentId on the client side
            const existingAdjustments = allAdjustments.filter(adjustment =>
                adjustment.masimsstockadjustmentId.trim() === adId.trim()
            );

            // Determine the next adjustment number based on the length of the filtered array
            const nextAdjustmentNo = existingAdjustments.length + 1;

            // POST request payload for masimsstockadjustmentaudits with the calculated serial number
            const postAuditPayload = {
                id: "",
                isdeleted: false,  // Adjusted to false for a valid record
                adjustmentno: nextAdjustmentNo,
                adjustmentdate: adjustmentDate,  // Use properly formatted ISO date
                physicalquantity: stockAdjustment.physicalQuantity,
                variance: stockAdjustment.variance,
                adjustmentquantity: stockAdjustment.adjustmentQuantity,
                currentstocklevel: cSL,
                masimsstockadjustmentId: adId
            };

            // POST request to create the new stock adjustment audit
            const postAuditUrl = `${MasterGraph}/api/masimsstockadjustmentaudits`;
            await axios.post(postAuditUrl, postAuditPayload, config);

            // Stock update
            const putAuditUpdatePayload = {
                id: srId,
                currentstocklevel: stockAdjustment.physicalQuantity
            };
            const putAuditUpdateUrl = `${MasterGraph}/api/GraphStockRegister/UpdateStockAdjustmentaudit/${srId}`;
            await axios.put(putAuditUpdateUrl, putAuditUpdatePayload, config);

            // Update state or dispatch actions as needed
            dispatch(updateStockUpdate({
                adjustmentNo: nextAdjustmentNo,
                adjustmentDate: dayjs(adjustmentDate).format("DD/MM/YYYY"),  // Keep formatted date for display
                physicalQuantity: stockAdjustment.physicalQuantity,
                variance: stockAdjustment.variance,
                adjustmentQuantity: stockAdjustment.adjustmentQuantity
            }));

            // Fetch grid rows again after saving
            fetchGridRows(adId);

            // Open success dialog
            setSuccessDialogOpen(true);

        } catch (error) {
            console.error("Error updating stock Adjustment data:", error);
        }
    };


    const handleSubmit = (e) => {
        e.preventDefault();
        let formIsValid = true;
        const newErrors = {
            physicalQuantity: false
        };
        const newErrorMessages = {
            physicalQuantity: ""
        };
        if (!stockAdjustment.physicalQuantity) {
            formIsValid = false;
            newErrors.physicalQuantity = true;
            newErrorMessages.physicalQuantity = "Physical Quantity is required";
        }
        setErrors(newErrors);
        setErrorMessages(newErrorMessages);

        if (formIsValid) {
            handleSave();
            setHasUnsavedChanges(false);
            setUnsavedChanges(false);
        }

    };

    const handleCancel = () => {

        if (!isViewMode && hasUnsavedChanges) {
            setCancelDialogOpen(true);
        } else {
            dispatch(resetEditStockAdjustment()); // Reset state on cancel
            navigate(-1, { replace: true });
        }
    };
    const handleCancelDialogClose = () => {
        setCancelDialogOpen(false);
    };

    const handleCancelDialogConfirm = () => {
        setCancelDialogOpen(false);
        navigate(-1, { replace: true });
    };
    const handleFieldChange = (field, value) => {
        setHasUnsavedChanges(true);
        setUnsavedChanges(true);
        dispatch(updateStockAdjustment({ [field]: value }));
    };

    const handleAdjustmentNoChange = (newValue) => {
        handleFieldChange('adjustmentNo', newValue);
    };

    const handleAdjustmentDateChange = (newValue) => {
        const formattedDate = formatDateForDisplay(newValue);
        handleFieldChange('adjustmentDate', formattedDate);
    };


    const handlePhysicalQuantityChange = (newValue) => {
        handleFieldChange('physicalQuantity', newValue);
        if (newValue) {
            setErrors((prevErrors) => ({
                ...prevErrors,
                physicalQuantity: false,
            }));
            setErrorMessages((prevMessages) => ({
                ...prevMessages,
                physicalQuantity: "",
            }));
        }
        else {
            setErrors((prevErrors) => ({
                ...prevErrors,
                physicalQuantity: true,
            }));
            setErrorMessages((prevMessages) => ({
                ...prevMessages,
                physicalQuantity: "Physical Quantity is required",
            }));
            // Clear adjustmentQuantity and variance when physicalQuantity is cleared
            handleFieldChange('adjustmentQuantity', '');
            handleFieldChange('variance', '');
        }
    };

    return (
        <>
            <DefaultCard sx={{ height: "100%" }}>
                <form onSubmit={handleSubmit} style={{ position: "relative" }}>
                    <Box mb={2}>
                        <Grid container gap={3}>
                            <Grid item xs={6} md={3.8}>
                                <CustomTextField
                                    label="Item Code"
                                    id="item-code"
                                    value={itemcode}
                                    disabled
                                />
                            </Grid>
                            <Grid item xs={6} md={3.8}>
                                <CustomTextField
                                    label="Item Description"
                                    id="item-code"
                                    value={itemCodeDesc}
                                    disabled
                                />
                            </Grid>
                            <Grid item xs={6} md={3.8}>
                                <CustomTextField
                                    label="Quantity in System"
                                    id="q-s"
                                    value={stockAdjustment.quantityInSystem}
                                    disabled
                                />
                            </Grid>
                            <Grid item xs={6} md={3.8}>
                                <CustomTextField
                                    maxlength={10}
                                    label="Physical Quantity"
                                    id="physical-quantity"
                                    value={stockAdjustment.physicalQuantity}
                                    onChange={(e) => handlePhysicalQuantityChange(e)}
                                    disabled={isViewMode}
                                    isMandatoryField={true}
                                    error={errors.physicalQuantity}
                                    errorMessage={errorMessages.physicalQuantity}
                                />
                            </Grid>

                            <Grid item xs={6} md={3.8}>
                                <CustomTextField
                                    label="Variance"
                                    id="variance"
                                    value={stockAdjustment.variance}
                                    disabled
                                />
                            </Grid>
                            <Grid item xs={6} md={3.8}>
                                <CustomTextField
                                    label="Adjustment Quantity"
                                    id="adjustment-quantity"
                                    value={stockAdjustment.adjustmentQuantity}
                                    disabled
                                />
                            </Grid>
                            <Grid item xs={6} md={3.8}>
                                <CustomDatePicker
                                    label="Adjustment Date"
                                    id="adjustment-date"
                                    value={formatDateForDisplay(currentDate)}
                                    onDateChange={handleAdjustmentDateChange}
                                    maxDate={currentDate}
                                    disabled={isViewMode}
                                />
                            </Grid>
                        </Grid>
                    </Box>
                </form>
                {isViewMode ? (
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                        <Button variant="outlined" sx={{ m: 1 }} onClick={handleCancel}>
                            Back
                        </Button>
                    </Box>

                ) : (
                    <CustomFooter
                        cancelClick={handleCancel}
                        saveClick={handleSubmit}
                        buttonLabel="Update"
                    />
                )}
                <Box mb={3} />
                <DefaultCard>
                    <Box
                        mb={1}
                        sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "flex-end",
                        }}
                    >
                        <SectionTitle title="Stock Adjustment List" />

                    </Box>
                    <CustomAgGrid
                        rows={rows}
                        columns={columns}
                        setGridApi={setGridApi}
                    />

                </DefaultCard>
                <CustomDialog
                    open={successDialogOpen}
                    onClose={() => setSuccessDialogOpen(false)}
                    content={succesDialogMessage}
                    confirmBtn="OK"
                    onConfirm={() => setSuccessDialogOpen(false)}
                />
                <CustomDialog
                    open={cancelDialogOpen}
                    onClose={handleCancelDialogClose}
                    content="Record(s) not Saved/Updated. Do you want to continue?"
                    confirmBtn="Yes"
                    cancelBtn="No"
                    onConfirm={handleCancelDialogConfirm}
                    onCancel={handleCancelDialogClose}
                />
            </DefaultCard>

        </>
    );
};

export default StockAdjustmentEditTab;
